import {useCallback, useEffect, useRef, useState} from 'react';
import {telephonicService} from '../../services/telephonicService';

const PHONE_PAGE_SIZE = 25;

const EMPTY_STATE: Record<string, any> = {
  calls: [],
  cursor: null,
  hasMore: false,
  isLoading: false,
  loadingMore: false,
  error: null,
  hasLoaded: false,
};

// Phone calls are company-shared and loaded lazily, with no schedule seed to
// paint from (unlike encounter history). A short-lived module cache lets the
// list render instantly on revisit / remount instead of popping in after a
// fresh network round-trip every time the Recent tab is opened.
// Stale-while-revalidate: the cached page is only the first paint — every
// (re)mount still refetches in the background and overwrites it, so a call
// that just ended shows up on return instead of after the TTL.
const PAGE_CACHE = new Map();
const PAGE_CACHE_TTL_MS = 5 * 60 * 1000;
const PAGE_CACHE_LIMIT = 20;

export const clearPlannerPhoneCallsCache = () => {
  PAGE_CACHE.clear();
};

// ── Cross-surface patient-link propagation ──────────────────────────────────
//
// The phone-call list is a cached one-shot fetch (no Firestore listener), so a
// patient linked to a conversation elsewhere — the telephone-encounter detail's
// patient picker, or the phone-call detail's "Link Patient" — wouldn't show up
// until a manual refresh. This tiny pub/sub lets those surfaces broadcast the
// link so every mounted list AND the module cache patch the call's patient
// fields in real time. (Persistence to the conversation doc is the caller's
// job, via `patientService.reassignSessionPatient`; this only keeps the UI in
// sync with that write.)

interface PhoneCallPatientPatch {
  patient_id?: string | null;
  ehr_patient_id?: string | null;
  patient_name?: string | null;
}

type PhoneCallPatientLinkListener = (
  callId: string,
  patch: PhoneCallPatientPatch,
) => void;

const patientLinkListeners = new Set<PhoneCallPatientLinkListener>();

function applyPatientPatch(
  calls: any[],
  callId: string,
  patch: PhoneCallPatientPatch,
): any[] {
  let changed = false;
  const next = calls.map(call => {
    if (!call || call.id !== callId) return call;
    changed = true;
    return {
      ...call,
      patient_id: patch.patient_id ?? null,
      ehr_patient_id: patch.ehr_patient_id ?? null,
      patient_name: patch.patient_name ?? null,
    };
  });
  return changed ? next : calls;
}

// Broadcast a patient link/unlink for a conversation. Patches the cache so a
// later remount paints it, then notifies every live list/detail subscriber.
export function notifyPhoneCallPatientLink(
  callId: string | null | undefined,
  patch: PhoneCallPatientPatch,
): void {
  if (!callId) return;
  PAGE_CACHE.forEach((cached, key) => {
    const patched = applyPatientPatch(cached.calls || [], callId, patch);
    if (patched !== cached.calls) {
      PAGE_CACHE.set(key, {...cached, calls: patched});
    }
  });
  patientLinkListeners.forEach(listener => {
    try {
      listener(callId, patch);
    } catch (_) {
      /* a misbehaving listener must not break the others */
    }
  });
}

// Subscribe to patient-link broadcasts (e.g. a phone-call detail keeping its
// header live while it's open). Returns an unsubscribe function.
export function subscribePhoneCallPatientLink(
  listener: PhoneCallPatientLinkListener,
): () => void {
  patientLinkListeners.add(listener);
  return () => {
    patientLinkListeners.delete(listener);
  };
}

const writeCache = (key: string, snapshot: Record<string, any>) => {
  if (!key) return;
  if (PAGE_CACHE.has(key)) PAGE_CACHE.delete(key);
  PAGE_CACHE.set(key, {
    calls: snapshot.calls || [],
    cursor: snapshot.cursor || null,
    hasMore: Boolean(snapshot.hasMore),
    cachedAt: Date.now(),
  });
  while (PAGE_CACHE.size > PAGE_CACHE_LIMIT) {
    const oldestKey = PAGE_CACHE.keys().next().value;
    if (!oldestKey) break;
    PAGE_CACHE.delete(oldestKey);
  }
};

const readCache = (key: string) => {
  if (!key) return null;
  const cached = PAGE_CACHE.get(key);
  if (!cached) return null;
  if (Date.now() - (cached.cachedAt || 0) >= PAGE_CACHE_TTL_MS) {
    PAGE_CACHE.delete(key);
    return null;
  }
  PAGE_CACHE.delete(key);
  PAGE_CACHE.set(key, cached);
  return cached;
};

const stateFromCache = (cached: Record<string, any>) => ({
  calls: cached.calls || [],
  cursor: cached.cursor || null,
  hasMore: Boolean(cached.hasMore),
  isLoading: false,
  loadingMore: false,
  error: null,
  hasLoaded: true,
});

export default function usePlannerPhoneCalls({
  companyId,
  userId = null,
  enabled = true,
}: Record<string, any> = {}) {
  const [state, setState] = useState(EMPTY_STATE);
  const requestIdRef = useRef(0);
  // Key the cache by user as well as company: the telephonic list is now
  // owner-scoped server-side (a provider sees company-shared calls + their own,
  // never a colleague's outbound/owned calls), so a company-only cache key
  // could otherwise serve one provider another's scoped results after a
  // same-session account switch.
  const cacheKey = companyId
    ? `phone:${companyId}:${userId || 'anon'}:${PHONE_PAGE_SIZE}`
    : '';

  // `background` is the revalidate-behind-a-cached-paint mode: no loading
  // flip on the way in, and a failure keeps the painted list (a background
  // refresh must never replace data with an error blank).
  const loadFirstPage = useCallback(
    async (background = false) => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      if (!enabled || !companyId) {
        setState(EMPTY_STATE);
        return [];
      }

      if (!background) {
        setState(prev => ({
          ...prev,
          isLoading: true,
          loadingMore: false,
          error: null,
        }));
      }

      try {
        const {conversations, nextCursor, hasMore} =
          await telephonicService.getCompanyTelephonicConversations({
            limit: PHONE_PAGE_SIZE,
          });
        if (requestIdRef.current !== requestId) return [];
        const calls = conversations || [];
        const nextState = {
          calls,
          cursor: nextCursor,
          hasMore: Boolean(hasMore),
          isLoading: false,
          loadingMore: false,
          error: null,
          hasLoaded: true,
        };
        setState(nextState);
        writeCache(cacheKey, nextState);
        return calls;
      } catch (err) {
        if (requestIdRef.current !== requestId) return [];
        console.error('Error loading planner phone calls:', err);
        if (!background) {
          setState({
            ...EMPTY_STATE,
            isLoading: false,
            error: err,
            hasLoaded: true,
          });
        }
        return [];
      }
    },
    [cacheKey, companyId, enabled],
  );

  const refresh = useCallback(() => loadFirstPage(false), [loadFirstPage]);

  useEffect(() => {
    if (!enabled || !companyId) {
      setState(EMPTY_STATE);
      return undefined;
    }

    // Stale-while-revalidate: paint the cached page immediately when there is
    // one, then always refetch so the list reflects calls made since.
    const cached = readCache(cacheKey);
    if (cached) {
      setState(stateFromCache(cached));
    }
    loadFirstPage(Boolean(cached));
    return () => {
      requestIdRef.current += 1;
    };
  }, [cacheKey, companyId, enabled, loadFirstPage]);

  // Patch this list in place when a patient is linked to one of its calls from
  // another surface, so the row + any derived detail reflect it immediately.
  useEffect(() => {
    const listener = (callId: string, patch: PhoneCallPatientPatch) => {
      setState(prev => {
        const patched = applyPatientPatch(prev.calls, callId, patch);
        return patched === prev.calls ? prev : {...prev, calls: patched};
      });
    };
    patientLinkListeners.add(listener);
    return () => {
      patientLinkListeners.delete(listener);
    };
  }, []);

  const loadMore = useCallback(async () => {
    if (
      !enabled ||
      !companyId ||
      !state.hasMore ||
      state.loadingMore ||
      !state.cursor
    )
      return;
    const requestId = requestIdRef.current;
    const cursor = state.cursor;
    setState(prev => ({...prev, loadingMore: true, error: null}));
    try {
      const {conversations, nextCursor, hasMore} =
        await telephonicService.getCompanyTelephonicConversations({
          cursor,
          limit: PHONE_PAGE_SIZE,
        });
      if (requestIdRef.current !== requestId) return;
      setState(prev => {
        const seen = new Set(prev.calls.map((call: any) => call.id));
        const additions = (conversations || []).filter(
          (call: any) => !seen.has(call.id),
        );
        const nextState = {
          ...prev,
          calls: [...prev.calls, ...additions],
          cursor: nextCursor,
          hasMore: Boolean(hasMore),
          loadingMore: false,
          error: null,
          hasLoaded: true,
        };
        writeCache(cacheKey, nextState);
        return nextState;
      });
    } catch (err) {
      console.error('Error loading more planner phone calls:', err);
      setState(prev => ({
        ...prev,
        loadingMore: false,
        error: err,
      }));
    }
  }, [
    cacheKey,
    companyId,
    enabled,
    state.cursor,
    state.hasMore,
    state.loadingMore,
  ]);

  return {
    calls: state.calls,
    hasMore: state.hasMore,
    isLoading: state.isLoading,
    loadingMore: state.loadingMore,
    error: state.error,
    hasLoaded: state.hasLoaded,
    refresh,
    loadMore,
  };
}
